import { useCallback, useEffect, useState } from 'react'

import type { NDParamsType } from 'commons'
import useParams from 'commons/customHooks/useParams'
import { LoadingModal, type OptionProps, type OrderTypesProps, Page } from 'components'
import { ActionTabs, type ItemActionTabsType } from 'components/Structures/ActionTabs'
import { Channels, defaultOfficeOptions, defaultOvTypeOptions, OVTypes } from 'constants/enums'
import { getOvTypes } from 'services/manage_ov'
import { getListaAnoMesCota } from 'services/nd'
import { getSaleRegions, getSaleTeam } from 'services/salesOrder'
import type { SaleRegion } from 'services/salesOrder/types'
import { asyncGetNegotiations, resetPagination } from 'store/NegotiationsContext/negotiationsSlice'
import { useDispatch, useSelector } from 'store/store'

import { Filter } from './components/Filter'
import NDTable from './components/NDTable'
import { Statistics } from './components/Statistics'
import * as S from './styles'
import { formattingNDPayload } from './utils'

type MesCotaType = {
  dt_ano_mes_cota: string
  ds_ano_mes_cota: string
}

export type SortFilterType = {
  column?: string
  isCrescent?: boolean
}

export const NDPage = () => {
  const dispatch = useDispatch()
  const { getParams, createParamsByObject } = useParams()

  const [ovTypes, setOvTypes] = useState<string[]>([] as string[])
  const [optionsMesCota, setOptionsMesCota] = useState<string[]>([] as string[])
  const [optionsOffices, setOptionsOffices] = useState<string[]>([])
  const [optionsOvTypes, setOptionsOvTypes] = useState<string[]>([] as string[])
  const [optionsSalesRegion, setOptionsSalesRegion] = useState<SaleRegion[]>([])
  const [optionsSalesTeam, setOptionsSalesTeam] = useState<OptionProps[]>([])

  const [NDFilterDefaultValues, setNDFilterDefaultValues] = useState({
    causas: 'Prazo,Desconto,Prazo + Desconto,Bonificação',
    codigosEquipeVenda: '',
    codigosRegiaoVenda: '',
    codigosSapCliente: '',
    currentTab: '0',
    escritoriosVenda: optionsOffices.join(','),
    hierarquiaCliente: 'false',
    material: '',
    mesesCota: '',
    numerosOrdemVenda: '',
    numerosOrdemVendaSap: '',
    razoesSociaisCliente: '',
    statusAprovacao: 'Aprovado,Não aprovado',
    tiposMaterial: 'Fora de linha,Em linha',
    tiposOrdemVenda: '',
  })

  useEffect(() => {
    return setOptionsOffices(
      defaultOfficeOptions.filter((item) => item.market === Channels.MI).map((item) => item.label),
    )
  }, [])

  const getSearchParams = () => {
    const params = getParams()
    return {
      ...NDFilterDefaultValues,
      ...params,
      codigosEquipeVenda: params.codigosEquipeVenda ? params.codigosEquipeVenda : '',
      codigosRegiaoVenda: params.codigosRegiaoVenda ? params.codigosRegiaoVenda : '',
      mesesCota: params.mesesCota ? params.mesesCota : optionsMesCota.join(','),
    }
  }

  useEffect(() => {
    return () => {
      dispatch(resetPagination())
    }
  }, [dispatch])

  const getSalesOptions = async () => {
    const regionsData = await getSaleRegions()
    if (regionsData) {
      const regionsOptions = regionsData.data.filter((regions) => !!regions.cd_regi_vend)
      setOptionsSalesRegion(regionsOptions)
    }

    const teamData = await getSaleTeam()
    if (teamData) {
      const teamOptions = teamData.data
        .filter((team) => !!team.cd_equi_vend)
        .map((team) => ({
          title: team.ds_equi_vend,
          value: team.cd_equi_vend,
        }))
      setOptionsSalesTeam(teamOptions)
    }
  }

  const getAsyncOptions = async () => {
    const quotaOptions = await getListaAnoMesCota()
    const dates = quotaOptions.map((option: MesCotaType) => option.ds_ano_mes_cota)
    setOptionsMesCota(dates)
    setNDFilterDefaultValues({
      ...NDFilterDefaultValues,
      mesesCota: dates.join(','),
    })

    await getOvTypes().then((response) => {
      const ovTypes = response?.data.map((item: OrderTypesProps) => item.ds_tipo_orde)
      const typesToFilter = defaultOvTypeOptions.filter(
        (type) => ![OVTypes.YTRA, OVTypes.YRBA].includes(type.value),
      )
      const filteredTypes = ovTypes.filter((ovType: string) =>
        typesToFilter.some((type) => ovType.includes(type.value)),
      )

      setOvTypes(filteredTypes)
      setOptionsOvTypes(filteredTypes)
      setNDFilterDefaultValues({
        ...NDFilterDefaultValues,
        mesesCota: dates.join(','),
        tiposOrdemVenda: filteredTypes.join(','),
      })
    })
  }

  useEffect(() => {
    getSalesOptions()
    getAsyncOptions()
  }, [])

  const {
    loadingNDs,
    negotiationsStatistics,
    sortFilter,
    pagination: { currentPage },
  } = useSelector((store) => store.negotiations)

  useEffect(() => {
    dispatch(asyncGetNegotiations(formattingNDPayload(getSearchParams()), currentPage, sortFilter))
  }, [sortFilter, dispatch, currentPage])

  const onFilterSubmit = useCallback(
    (data: NDParamsType) => {
      dispatch(asyncGetNegotiations(formattingNDPayload(data), 1, sortFilter))
    },
    [dispatch, sortFilter],
  )

  const onTabClick = (ovTypesValue: string[], index: string) => {
    setOvTypes(ovTypesValue)
    createParamsByObject({
      ...getSearchParams(),
      currentTab: index,
      tiposOrdemVenda: ovTypesValue.join(','),
    })
    onFilterSubmit({
      ...getSearchParams(),
      tiposOrdemVenda: ovTypesValue.join(','),
    })
  }

  const NDTabsItems: ItemActionTabsType[] = [
    {
      label: 'Todas',
      onClick: (index) => onTabClick(optionsOvTypes, index),
    },
    {
      label: 'NDs',
      onClick: (index) =>
        onTabClick([OVTypes.YPEM, OVTypes.YCPM, OVTypes.YFRQ, OVTypes.YHFQ], index),
    },
    {
      label: 'Bonificações',
      onClick: (index) => onTabClick([OVTypes.YRBA], index),
    },
  ]

  return (
    <Page fullwidth>
      {loadingNDs ? <LoadingModal /> : null}
      <>
        <Filter
          defaultValues={NDFilterDefaultValues}
          getSearchParams={getSearchParams}
          onSubmit={onFilterSubmit}
          optionsMesCota={optionsMesCota}
          optionsOffices={optionsOffices}
          optionsOvTypes={optionsOvTypes}
          optionsSalesRegion={optionsSalesRegion}
          optionsSalesTeam={optionsSalesTeam}
          ovTypes={ovTypes}
        />

        <S.NdCard>
          <ActionTabs currentItem={getSearchParams().currentTab} items={NDTabsItems} />
          {!!Object.keys(negotiationsStatistics).length && <Statistics />}

          <NDTable payload={formattingNDPayload(getSearchParams())} />
        </S.NdCard>
      </>
    </Page>
  )
}

export default NDPage
